import { StorePersistService } from './checkout/modules/checkout-shared/services/store-persist.service';
import { DialogService } from './dialog/services/dialog.service';
import { AuthService } from './shared/services/auth.service';
import { FaultCode, FaultCodeService } from './shared/services/fault-code.service';
import { SeoService } from './shared/services/seo.service';
import { ViewportService } from './shared/services/viewport.service';
import { darkenColor, firstTruthy, lightenColor, setCssColors } from './shared/utils/util';
import { getUser, isLoggedUser } from './user/reducers/user.reducer';
import { animate, query, style, transition, trigger } from '@angular/animations';
import { Component, OnInit, Renderer2, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { RootReducer, Store, getShowContent, getShowGlobalSpinner } from '@app/app.reducers';
import {
	CONFIRM_URL,
	LOGIN_URL,
	REGISTRATION_STANDALONE,
	RESET_PASSWORD_URL,
} from '@app/app.router.urls';
import { ProductListPersistService } from '@app/checkout/modules/checkout-shared/services/product-list-persist.service';
import { StoreColors, getStoreColors } from '@app/shared/reducers/storefront.reducer';
import { ToastService } from '@app/shared/services/toast.service';
import { doScrolling } from '@app/shared/utils/scroll-to-helper';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EMPTY, Observable, combineLatest, of, timer } from 'rxjs';
import { debounce, filter, map, shareReplay, switchMap } from 'rxjs/operators';

export const WAIT_BEFORE_LOADING_SPINNER = 1000;
export const USER_LOGGED_IN_CLASS_NAME = 'is-user-logged-in';

const fadeIn = [style({ opacity: 0 }), animate('200ms', style({ opacity: 1 }))];
const fadeOut = [style({ opacity: 1 }), animate('200ms', style({ opacity: 0 }))];

/** App root component */
@UntilDestroy()
@Component({
	selector: 'g-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	animations: [
		trigger('spinnerFade', [transition(':enter', fadeIn), transition(':leave', fadeOut)]),
		trigger('routeFadeAnimation', [
			transition('* => *', [
				query(':enter', [style({ opacity: 0 })], { optional: true }),
				query(':enter', fadeIn, { optional: true }),
			]),
		]),
	],
	standalone: false,
})
export class AppComponent implements OnInit {
	private readonly router = inject(Router);
	private readonly store = inject<Store<RootReducer.State>>(Store);
	private readonly route = inject(ActivatedRoute);
	private readonly toastService = inject(ToastService);
	private readonly dialogService = inject(DialogService);
	private readonly storePersistService = inject(StorePersistService);
	private readonly productListPersistService = inject(ProductListPersistService);
	private readonly authService = inject(AuthService);
	private readonly faultCodeService = inject(FaultCodeService);
	private readonly renderer = inject(Renderer2);
	readonly seoService = inject(SeoService);
	readonly viewport = inject(ViewportService);

	readonly showContent$: Observable<boolean>;
	readonly showHeader$: Observable<boolean>;
	readonly showVignetteHeader$: Observable<boolean>;
	readonly showFooter$: Observable<boolean>;
	readonly showNotificationBanner$: Observable<boolean>;
	readonly showCustomHtml$: Observable<boolean>;
	readonly faultCode$: Observable<FaultCode>;
	readonly onDeactivate = doScrolling.bind(undefined, 0, 100);
	readonly showSpinner$!: Observable<boolean>;

	constructor() {
		this.dialogService.init();
		this.toastService.init();
		this.authService.init();
		this.seoService.setDirectly({}); // Set default SEO values.
		// TODO: Move to checkout module.
		void this.storePersistService.init();
		this.productListPersistService.init();
		this.showContent$ = this.store.select(getShowContent);

		const getBoolProp$ = (prop: string, defaultTo = false) =>
			this.router.events.pipe(
				// ActivatedRoute is not correct for AppComponent,
				// we need to wait for the navigation to finish and return the child route.
				filter((event) => event instanceof NavigationEnd),
				switchMap(() => {
					let route = this.route;
					while (route.firstChild) route = route.firstChild;
					return route.data;
				}),
				map<{ [prop: string]: boolean }, boolean>(({ [prop]: val }) => val ?? defaultTo),
				filter<boolean>((val) => val === undefined || typeof val === 'boolean'),
			);

		this.showNotificationBanner$ = getBoolProp$('showNotificationBanner');
		this.showCustomHtml$ = getBoolProp$('showCustomHtml');
		this.showVignetteHeader$ = getBoolProp$('showVignetteHeader', true);
		this.showHeader$ = getBoolProp$('showHeader', true);
		this.showFooter$ = getBoolProp$('showFooter', true);

		this.faultCode$ = this.faultCodeService.getFaultCode$();
	}

	/** Initialize component */
	ngOnInit(): void {
		const routerUrls$ = this.router.events.pipe(
			filter((event): event is NavigationEnd => event instanceof NavigationEnd),
			map((val) => val?.url),
			shareReplay(1),
		);
		// Show loading spinner until content has been fetched
		// TODO: Handle using router resolve somehow?
		// @ts-expect-error -- readonly
		this.showSpinner$ = combineLatest([this.store.select(getShowGlobalSpinner), routerUrls$]).pipe(
			map(
				([showGlobalSpinner, url]) =>
					showGlobalSpinner &&
					!url.startsWith(LOGIN_URL) &&
					!url.startsWith(CONFIRM_URL) &&
					!url.startsWith(REGISTRATION_STANDALONE) &&
					!url.startsWith(RESET_PASSWORD_URL),
			),
			// Only show spinner if loading takes long.
			debounce((showSpinner) => (showSpinner ? timer(WAIT_BEFORE_LOADING_SPINNER) : of(EMPTY))),
		);

		// Set primary color custom css properties
		this.store
			.select(getStoreColors)
			.pipe(firstTruthy)
			.subscribe((colors) => this.setStoreCustomColors(colors));

		// Remove initial loading spinner to stop it from animating forever with opacity 0.
		const spinner = document.querySelector('body > .loading');
		// istanbul ignore if
		if (spinner) setTimeout(spinner.remove.bind(spinner), 1000);

		this.store
			.select(getUser)
			.pipe(map(isLoggedUser), untilDestroyed(this))
			.subscribe((isUserLoggedIn) => this.setBodyTagClasses(isUserLoggedIn));
	}

	/** Set primary color custom css properties */
	private setStoreCustomColors(colors: StoreColors) {
		setCssColors({
			primary: colors.primary,
			'primary-text': colors.primaryText,
			'primary-highlight': `#${darkenColor(colors.primary, 0.08)}`,
			'primary-lighter': `#${lightenColor(colors.primary, 0.5)}`,
			'info-box': `#${lightenColor(colors.primary, 0.15)}`,
		});
	}

	/** Set body tag classes */
	private setBodyTagClasses(isUserLoggedIn: boolean) {
		if (isUserLoggedIn) {
			this.renderer.addClass(document.body, USER_LOGGED_IN_CLASS_NAME);
		} else if (document.body.classList.contains(USER_LOGGED_IN_CLASS_NAME)) {
			this.renderer.removeClass(document.body, USER_LOGGED_IN_CLASS_NAME);
		}
	}
}
